import _ from 'lodash';

/**
 * Calculate Monada Account Score (0–100)
 *
 * Components:
 *   Activity  (30%) — non-CW timeline updates + opp status changes in last 90 days
 *   Win Rate  (25%) — won / (won + lost) on sent opportunities
 *   Revenue   (25%) — total won revenue, normalized against orgMaxRevenue
 *   Recency   (20%) — days since last touchpoint (inverse)
 */
function calcAccountScore({ account, opportunities, exchangeRates, orgMaxRevenue, organization }) {
    const cur = organization?.defaults?.currency || 'USD';
    const now = Date.now();
    const DAY = 1000 * 60 * 60 * 24;
    const ninetyDaysAgo = now - 90 * DAY;

    const nonCwUpdates = (account.updates || []).filter(u =>
        !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected'
    );
    const allOpps = (opportunities || []).filter(o => o.status !== 'deleted');
    const sent = allOpps.filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
    const won = allOpps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted');
    const lost = allOpps.filter(o => o.status === 'lost' || o.status === 'endcustomer-rejected');

    // --- Activity (30%) ---
    // Count touchpoints in last 90 days. Cap at 5 for max score.
    // Touchpoints = timeline updates (non-CW) + sent opportunities (excludes drafts)
    const recentUpdates = nonCwUpdates.filter(u => u.created >= ninetyDaysAgo).length;
    const recentSentOpps = sent.filter(o => o.created >= ninetyDaysAgo).length;
    const activityCount = recentUpdates + recentSentOpps;
    const activityScore = Math.min(100, (activityCount / 5) * 100);

    // --- Win Rate (25%) ---
    // Won / Sent. Accounts with sent deals but no wins get partial credit.
    // Accounts with no opps at all get 0.
    const winRateScore = sent.length > 0
        ? (won.length / sent.length) * 100
        : allOpps.length > 0 ? 20 : 0;

    // --- Revenue (25%) ---
    // Logarithmic normalization against org's top account — prevents one outlier
    // from crushing everyone else's score
    let totalRevenue = 0;
    if (exchangeRates && organization) {
        for (const o of won) {
            const offer = o.offers?.find(o2 => o.selected?.includes(o2.id)) || o.offers?.[0];
            if (!offer) continue;
            const fieldValues = (offer.sections || []).flatMap(s =>
                (s.offers || []).flatMap(so =>
                    (so.fields || []).flatMap(f => {
                        if (f.type === 'flat' && f.values?.flat?.value != null) return [f.values.flat.value * (exchangeRates[cur] || 1) / (exchangeRates[f.values.flat.currency] || 1)];
                        if (f.values) return Object.values(f.values).filter(v => v?.value != null).map(v => v.value * (exchangeRates[cur] || 1) / (exchangeRates[v.currency] || 1));
                        return [];
                    })
                )
            );
            totalRevenue += _.sum(fieldValues.filter(v => isFinite(v) && !isNaN(v)));
        }
    }
    const revenueScore = orgMaxRevenue > 0 && totalRevenue > 0
        ? Math.min(100, (Math.log(totalRevenue + 1) / Math.log(orgMaxRevenue + 1)) * 100)
        : 0;

    // --- Recency (20%) ---
    // Days since last touchpoint. 0 days = 100, 180+ days = 0 (was 90 — too aggressive)
    const allTimestamps = [
        ...nonCwUpdates.map(u => u.created),
        ...allOpps.map(o => o.created),
        ...allOpps.flatMap(o => (o.log || []).filter(e => e.timestamp).map(e => e.timestamp)),
    ].filter(Boolean);
    const lastTouch = allTimestamps.length > 0 ? _.max(allTimestamps) : null;
    const daysSince = lastTouch ? Math.max(0, Math.round((now - lastTouch) / DAY)) : 999;
    const recencyScore = Math.max(0, Math.min(100, 100 - (daysSince / 180) * 100));

    // --- Weighted total ---
    const score = Math.round(
        activityScore * 0.30 +
        winRateScore * 0.25 +
        revenueScore * 0.25 +
        recencyScore * 0.20
    );

    const score10 = Math.round(Math.max(0, Math.min(100, score)) / 10);

    return {
        score: score10,
        components: { activity: Math.round(activityScore), winRate: Math.round(winRateScore), revenue: Math.round(revenueScore), recency: Math.round(recencyScore) },
        raw: { activityCount, wonCount: won.length, lostCount: lost.length, sentCount: sent.length, daysSince, totalRevenue },
    };
}

function getScoreColor(score) {
    if (score >= 8) return { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Hot' };
    if (score >= 5) return { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Warm' };
    if (score >= 2) return { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', label: 'Cool' };
    return { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Cold' };
}

/**
 * Compute orgMaxRevenue from all accounts + opportunities.
 * Call once, pass to each calcAccountScore call.
 */
function calcOrgMaxRevenue({ accounts, opportunities, exchangeRates, organization }) {
    const cur = organization?.defaults?.currency || 'USD';
    let maxRev = 0;
    for (const account of accounts || []) {
        const acctOpps = (opportunities || []).filter(o => o.account?.id === account.id && o.status !== 'deleted');
        const won = acctOpps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted');
        let rev = 0;
        for (const o of won) {
            const offer = o.offers?.find(o2 => o.selected?.includes(o2.id)) || o.offers?.[0];
            if (!offer) continue;
            const fieldValues = (offer.sections || []).flatMap(s =>
                (s.offers || []).flatMap(so =>
                    (so.fields || []).flatMap(f => {
                        if (f.type === 'flat' && f.values?.flat?.value != null) return [f.values.flat.value * (exchangeRates[cur] || 1) / (exchangeRates[f.values.flat.currency] || 1)];
                        if (f.values) return Object.values(f.values).filter(v => v?.value != null).map(v => v.value * (exchangeRates[cur] || 1) / (exchangeRates[v.currency] || 1));
                        return [];
                    })
                )
            );
            rev += _.sum(fieldValues.filter(v => isFinite(v) && !isNaN(v)));
        }
        if (rev > maxRev) maxRev = rev;
    }
    return maxRev;
}

function getScoreExplanation(score) {
    if (!score) return '';
    const { raw, components } = score;
    const sc = getScoreColor(score.score);
    const lines = [`${sc.label} account (${score.score}/10)`];

    // Activity
    if (raw.activityCount === 0) lines.push('No activity in the last 90 days');
    else if (raw.activityCount <= 2) lines.push(`Light activity — ${raw.activityCount} touchpoint${raw.activityCount > 1 ? 's' : ''} in the last 90 days`);
    else if (raw.activityCount <= 5) lines.push(`Good activity — ${raw.activityCount} touchpoints in the last 90 days`);
    else lines.push(`High activity — ${raw.activityCount} touchpoints in the last 90 days`);

    // Win rate (quotes = opportunities)
    if (raw.sentCount === 0) lines.push('No quotes sent yet');
    else if (raw.wonCount === 0 && raw.lostCount === 0) lines.push(`${raw.sentCount} quote${raw.sentCount > 1 ? 's' : ''} sent, awaiting response — looking promising`);
    else if (raw.wonCount === 0) lines.push(`${raw.lostCount} quote${raw.lostCount > 1 ? 's' : ''} lost out of ${raw.sentCount} sent — no wins yet`);
    else {
        const winPct = Math.round(raw.wonCount / raw.sentCount * 100);
        const pending = raw.sentCount - raw.wonCount - raw.lostCount;
        let detail = `${raw.wonCount} quote${raw.wonCount > 1 ? 's' : ''} won, ${raw.lostCount} lost`;
        if (pending > 0) detail += `, ${pending} pending`;
        lines.push(`${detail} — out of ${raw.sentCount} sent (${winPct}% conversion)`);
    }

    // Recency
    if (raw.daysSince === 0) lines.push('Contacted today');
    else if (raw.daysSince <= 7) lines.push(`Last contact ${raw.daysSince} day${raw.daysSince > 1 ? 's' : ''} ago`);
    else if (raw.daysSince <= 30) lines.push(`Last contact ${raw.daysSince} days ago — could use a follow-up`);
    else if (raw.daysSince <= 90) lines.push(`Last contact ${raw.daysSince} days ago — going cold`);
    else lines.push('No recent contact — account is dormant');

    return lines;
}

export { calcAccountScore, getScoreColor, calcOrgMaxRevenue, getScoreExplanation };
